import React from 'react';
import {useTranslation} from 'react-i18next';
import useSWR from 'swr';
import {StripeError} from '@stripe/stripe-js';
import api, {getURL} from 'api';
import {
  GUEST_APP_EVENTS_NAME,
  PAYMENT_PROVIDERS,
  RESERVATION_PAYMENT_TYPES,
  WS_EVENT_TYPES,
} from 'utils/constants';
import {Statuses, useIsMounted, useStatus} from 'utils/hooks';
import {useStripeCardElement} from 'components/StripeCardElement';
import {usePaymentSettings} from './usePaymentSettings';
import {useWebsocket} from 'context/websocket';
import {useReservation} from 'context/reservation';
import {UseStripeCardElementReturn} from '../components/StripeCardElement/useStripeCardElement';
import useMixPanel from './useMixPanel';

const emptyFunction = () => {};

enum PAYMENT_TYPES {
  reservationGeneral = 'RESERVATION_GENERAL',
}
const DEFAULT_DEPOSIT_AMOUNT = '0.00';

const getStripePaymentPayload = (reservation_id = '') => {
  return {
    reservation_id,
    type: PAYMENT_TYPES.reservationGeneral,
  };
};

const getStripeDepositPaymentPayload = (reservation_id = '', amount: number | string) => {
  return {
    amount,
    reservation_id,
  };
};

function getSecurityDeposit(reservation?: any) {
  if (!reservation) {
    return DEFAULT_DEPOSIT_AMOUNT;
  }

  return reservation?.security_deposit_amount || DEFAULT_DEPOSIT_AMOUNT;
}

function fetchGuestPayment(reservation: any) {
  const reservationId = reservation?.id;
  if (!reservationId) return null;
  return getURL(api.guestPayments.ENDPOINTS.forTaxes(`reservation_id=${reservationId}`));
}

enum GUEST_PAYMENT_SUCCESS_STATUSES {
  secureWaiting = 'SECURE_WAITING',
  new = 'NEW',
  completed = 'COMPLETED',
  failed = 'FAILED',
}

type GuestPayment = {
  status: GUEST_PAYMENT_SUCCESS_STATUSES;
  amount: string;
  created_at: string;
  description: string;
  id: string;
  ip_address: string;
  reservation_id: string;
  secure_url: string;
  security_deposit: null | string;
  security_deposit_id: null | string;
  status_details: string;
  type: 'RESERVATION_TAXES';
};

export enum PaymentTypes {
  guest = 'guest',
  security_deposit = 'security_deposit',
}
type PaymentType = PaymentTypes.guest | PaymentTypes.security_deposit;
export type UsePaymentProps = {
  paymentType: PaymentType;
  onPaymentSuccess: (...a: any) => void;
  onPaymentError: (error: StripeError) => void;
  extraFields?: Record<string, unknown>;
};

type useWSHandlersProps = {
  onAuthCodeReceived?: (...a: any) => void;
  onPaymentCompleted?: (...a: any) => void;
  onPaymentFailed?: (...a: any) => void;
};
/**
 * WebSocket payment handlers
 * @param props
 */
function useWSHandlers(props: useWSHandlersProps = {}) {
  const ws = useWebsocket();

  const {
    onAuthCodeReceived = emptyFunction,
    onPaymentCompleted = emptyFunction,
    onPaymentFailed = emptyFunction,
  } = props;

  const handleWSEventsEffect = React.useCallback(() => {
    if (
      ws.message.event_type === WS_EVENT_TYPES.guestPaymentCompleted ||
      ws.message.event_type === WS_EVENT_TYPES.securityDepositConfirmed
    ) {
      onPaymentCompleted();
    }

    if (
      ws.message.event_type === WS_EVENT_TYPES.guestPaymentAuthCodeReceived ||
      ws.message.event_type === WS_EVENT_TYPES.securityDepositAuthCodeReceived
    ) {
      onAuthCodeReceived(ws.message.auth_code);
    }

    if (
      ws.message.event_type === WS_EVENT_TYPES.guestPaymentFailed ||
      ws.message.event_type === WS_EVENT_TYPES.securityDepositCancelled
    ) {
      onPaymentFailed(ws.message?.status_details, String(Date.now()));
    }

    return ws.clearMessage;
  }, [
    onAuthCodeReceived,
    onPaymentCompleted,
    onPaymentFailed,
    ws.clearMessage,
    ws.message,
  ]);

  React.useEffect(handleWSEventsEffect, [ws.message.event_type, handleWSEventsEffect]);
}

/**
 * Handlers for Guest payments
 * @param param0
 * @returns
 */
function useGuestPayments({onPaymentError}: Pick<UsePaymentProps, 'onPaymentError'>) {
  const isMounted = useIsMounted();
  const {data: reservation} = useReservation();
  const {mutate: mutateGuestPayment} = useSWR<GuestPayment>(
    fetchGuestPayment(reservation),
  );
  const {DispatchGeneralPayments} = useMixPanel();
  const createGuestPayment = React.useCallback(
    async (payload: any) => {
      const result = await api.guestPayments.post(payload);
      if (!isMounted.current) return;
      let status: boolean = true;
      if (result.data) {
        await mutateGuestPayment(result.data, false);
      }
      if (result.error) {
        status = false;
        onPaymentError?.(result.error);
      }
      DispatchGeneralPayments(status);
    },
    [isMounted, mutateGuestPayment, onPaymentError, DispatchGeneralPayments],
  );

  const handleGuestPayment = React.useCallback(async () => {
    const payload = getStripePaymentPayload(reservation?.id);
    await createGuestPayment(payload);
  }, [createGuestPayment, reservation]);

  return {handleGuestPayment};
}

/**
 * Handlers for Deposit payments
 * @param param0
 * @returns
 */
function useDepositPayments({onPaymentError}: Pick<UsePaymentProps, 'onPaymentError'>) {
  const {data: reservation} = useReservation();
  const depositAmount = getSecurityDeposit(reservation);
  const {PaymentEvents} = useMixPanel();

  const createOrUpdateStripeSecurityDeposit = React.useCallback(
    async (payload: any) => {
      const {error, data} = await api.securityDeposits.post(payload);
      let status: boolean = true;
      //Deposit Payment Endpoint
      if (error) {
        onPaymentError?.(error);
        status = false;
      }
      PaymentEvents(GUEST_APP_EVENTS_NAME.paymentCompleted, {
        type: RESERVATION_PAYMENT_TYPES.segurityDeposit,
        paymentId: data?.id,
        status: status,
      });
    },
    [onPaymentError, PaymentEvents],
  );

  const handleSecurityDeposit = React.useCallback(async () => {
    const payload = getStripeDepositPaymentPayload(reservation?.id, depositAmount);
    await createOrUpdateStripeSecurityDeposit(payload);
  }, [createOrUpdateStripeSecurityDeposit, depositAmount, reservation]);

  return {handleSecurityDeposit};
}

type DefaultFunctionType = (a?: any) => void;
type ConfirmIntentType = DefaultFunctionType;
type HandlePaymentSubmitType = DefaultFunctionType;

type UsePaymentFactoryReturn = {
  confirmIntent: ConfirmIntentType;
  handlePaymentSubmit: HandlePaymentSubmitType;
};
/**
 * Returns handlers depending on paymentType
 * @param paymentType
 * @param onPaymentError
 * @param cardElementState
 * @returns
 */
function usePaymentFactory(
  paymentType: PaymentType,
  cardElementState: UseStripeCardElementReturn,
  onPaymentError: (a: StripeError) => void,
): UsePaymentFactoryReturn {
  const {handleGuestPayment} = useGuestPayments({
    onPaymentError,
  });
  const {handleSecurityDeposit} = useDepositPayments({
    onPaymentError,
  });

  const guestFunctions = React.useMemo(
    () => ({
      handlePaymentSubmit: handleGuestPayment,
      confirmIntent: cardElementState.confirmPaymentIntent,
      splitCardElementsProps: cardElementState.splitCardElementsProps,
    }),
    [
      cardElementState.confirmPaymentIntent,
      cardElementState.splitCardElementsProps,
      handleGuestPayment,
    ],
  );
  const securityDepositFunctions = React.useMemo(
    () => ({
      handlePaymentSubmit: handleSecurityDeposit,
      confirmIntent: cardElementState.confirmDepositPaymentIntent,
      splitCardElementsProps: cardElementState.splitCardElementsProps,
    }),
    [
      cardElementState.confirmDepositPaymentIntent,
      cardElementState.splitCardElementsProps,
      handleSecurityDeposit,
    ],
  );
  const emptyFunctions = React.useMemo(
    () => ({
      handlePaymentSubmit: emptyFunction,
      confirmIntent: emptyFunction,
      splitCardElementsProps: emptyFunction,
      isStripeCardInvalid: false,
    }),
    [],
  );

  if (paymentType === PaymentTypes.guest) return guestFunctions;
  if (paymentType === PaymentTypes.security_deposit) return securityDepositFunctions;
  return emptyFunctions;
}

type ReturnedHandlers = {
  handleSubmit: () => void;
};

type UsePaymentReturnType = ReturnedHandlers & {
  isDisabled: boolean;
  isLoading: boolean;
  cardElementState: any;
};
/**
 * Payments entry point, decide the payment type
 * and provide a template/outline for payments with this provider
 * @param props
 * @returns
 */
function usePayment(props: UsePaymentProps): UsePaymentReturnType {
  const {t} = useTranslation();
  const {paymentType, onPaymentSuccess, onPaymentError, extraFields} = props;
  const {paymentProvider} = usePaymentSettings();
  const {refreshReservation} = useReservation();
  const {isLoading, setStatus} = useStatus();
  const [isSubmitDisabled, setIsSubmitDisabled] = React.useState<boolean>(false);

  const handlePaymentError = (error: StripeError) => {
    const defaultErrorMessage = t('bank_authentication_process_has_failed');
    onPaymentError(error || defaultErrorMessage);
    setStatus(Statuses.idle);
  };
  const cardElementState = useStripeCardElement(handlePaymentError, true, extraFields);
  const {handlePaymentSubmit, confirmIntent} = usePaymentFactory(
    paymentType,
    cardElementState,
    handlePaymentError,
  );
  const isCardInvalid = cardElementState.disabled;
  const isDisabled = isCardInvalid || isLoading;

  const handleSuccess = React.useCallback(async () => {
    await refreshReservation();
    setStatus(Statuses.success);
    onPaymentSuccess();
  }, [onPaymentSuccess, refreshReservation, setStatus]);

  useWSHandlers({
    onAuthCodeReceived: confirmIntent,
    onPaymentCompleted: handleSuccess,
  });

  const handleStripeSubmit = React.useCallback(() => {
    if (isCardInvalid) return;
    setStatus(Statuses.loading);
    handlePaymentSubmit();
    // Waiting for WS_EVENT_TYPES.guestPaymentAuthCodeReceived
  }, [handlePaymentSubmit, isCardInvalid, setStatus]);

  /**
   * When handling payment sometimes you will encounter error:
   * Unhandled Rejection (IntegrationError):
   *  - You have an in-flight confirmCardPayment!
   *  - Please be sure to disable your form submit button when confirmCardPayment is called.
   * This is because usePayment is used in several places in app
   * I've tried to resolve it with a singleton, but I think setIsSubmitDisabled is not
   * called in order that I need, what you could do is to rely on the `status` state
   *
   * This error is only in development and doesn't prevent payment
   * @param wrappe
   * @returns
   */
  const singleton = React.useCallback(
    (wrappe: (...args: any[]) => void) =>
      (...args: any[]) => {
        if (isSubmitDisabled) return emptyFunction();
        setIsSubmitDisabled(true);
        wrappe(...args);
        setIsSubmitDisabled(false);
      },
    [isSubmitDisabled],
  );

  const providersEventHandlersMapping = React.useMemo(
    () => ({
      [PAYMENT_PROVIDERS.stripe]: {handleSubmit: singleton(handleStripeSubmit)},
      [PAYMENT_PROVIDERS.paycomet]: {handleSubmit: singleton(emptyFunction)},
    }),
    [handleStripeSubmit, singleton],
  );

  const handlers: ReturnedHandlers = React.useMemo(
    () => providersEventHandlersMapping[paymentProvider],
    [paymentProvider, providersEventHandlersMapping],
  );

  return {isDisabled, cardElementState, isLoading, ...handlers};
}

export {usePayment};
